import type {
  CarrierAuditState,
  CarrierFolderPublicationProgress,
  CarrierInitializationCompanyGroup,
  CarrierInitializationMappingDrafts,
  CarrierRecordType,
} from '@/services/api';

export type CarrierMappingDraftValues = Record<string, string>;

export function createCarrierMappingDraftValues(
  groups: CarrierInitializationCompanyGroup[]
): CarrierMappingDraftValues {
  return Object.fromEntries(groups.map((group) => [
    String(group.providerCompanyId),
    group.providerCompanyName,
  ]));
}

function normalizedName(value: string): string {
  return value.trim().normalize('NFKC').toLocaleLowerCase('en-US').replace(/\s+/g, ' ');
}

export function validateCarrierNames(names: string[]): string | null {
  const normalizedNames = new Set<string>();
  for (const value of names) {
    const name = value.trim();
    if (!name) return 'Enter a name for every Carrier.';
    if (name === '.' || name === '..' || name.includes('/') || name.includes('\\')) {
      return 'Carrier names cannot contain folder separators.';
    }
    const normalized = normalizedName(name);
    if (normalizedNames.has(normalized)) return 'Each Carrier name must be unique.';
    normalizedNames.add(normalized);
  }
  return null;
}

export function validateCarrierMappingDraftValues(
  groups: CarrierInitializationCompanyGroup[],
  values: CarrierMappingDraftValues
): string | null {
  return validateCarrierNames(groups.map(
    (group) => values[String(group.providerCompanyId)] ?? ''
  ));
}

export function carrierFolderPreview(name: string): string {
  return `Carriers/${name.trim()}/`;
}

function countLabel(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function carrierFolderPublicationBlockedMessage(
  progress: Pick<CarrierFolderPublicationProgress, 'blockedReason' | 'disposition'>
): string | null {
  switch (progress.blockedReason) {
    case null:
      return null;
    case 'mapping_incomplete':
      return 'Finish finding carrier data before you publish Carrier folders.';
    case 'mapping_integrity_error':
      return 'Some Carrier links point to missing trunks or Service Types. Reload the Carrier setup before you publish.';
    case 'audit_running':
      return 'A Carrier audit is running. Let it finish before you publish.';
    case 'audit_conflict':
      return 'The Carrier audit found Switch Platform conflicts. Resolve them and run the audit again before you publish.';
    case 'audit_review_pending':
      return 'Prepare the Carrier audit review before you publish.';
    case 'disposition_incomplete': {
      const parts: string[] = [];
      if (progress.disposition.unassignedAccounts > 0) {
        parts.push(countLabel(progress.disposition.unassignedAccounts, 'account', 'accounts'));
      }
      if (progress.disposition.unassignedVendors > 0) {
        parts.push(countLabel(progress.disposition.unassignedVendors, 'vendor', 'vendors'));
      }
      const verb = progress.disposition.unassignedAccounts
        + progress.disposition.unassignedVendors === 1 ? 'is' : 'are';
      return `${parts.join(' and ')} ${verb} not assigned to a Carrier or ignored. Assign or ignore every trunk before you publish.`;
    }
    case 'review_required':
      return 'Confirm every Carrier mapping before you publish Carrier folders.';
  }
}

export function ignoredRecordLabel(recordType: CarrierRecordType): string {
  if (recordType === 'customer_company') return 'Inbound Carrier candidate';
  if (recordType === 'customer_account') return 'Inbound trunk';
  return 'Outbound trunk';
}

export interface CarrierAuditActionState {
  visible: boolean;
  enabled: boolean;
  label: string;
  reason: string | null;
}

// The Carrier audit is the read-only "check the Switch Platform" step inside
// /organize-carriers (ADR-0024 section 9). It starts only from published, current folders.
export function carrierAuditActionState(input: {
  mapping: Pick<CarrierInitializationMappingDrafts, 'ready' | 'drafts'> | null;
  folderPublication: Pick<CarrierFolderPublicationProgress, 'totalCarriers' | 'foldersCurrent' | 'disposition'> | null;
  audit: Pick<CarrierAuditState, 'status' | 'dispatch'> | null;
  dirty: boolean;
  activeSessionId: string | null;
}): CarrierAuditActionState {
  const hidden = { visible: false, enabled: false, label: '', reason: null };
  if (!input.mapping || !input.folderPublication || input.folderPublication.totalCarriers === 0) {
    return hidden;
  }
  const running = input.audit?.status === 'running';
  // Only the chat that started an audit can follow or resume it.
  if (running && input.audit?.dispatch && input.audit.dispatch.chatSessionId !== input.activeSessionId) {
    return {
      visible: true,
      enabled: false,
      label: 'Switch Platform check in another chat',
      reason: 'A Carrier audit is running from another chat. Open that chat to follow or resume it.',
    };
  }
  if (running && input.audit?.dispatch?.status === 'running') {
    return {
      visible: true,
      enabled: false,
      label: 'Checking the Switch Platform…',
      reason: 'The Carrier audit is running. Its result appears in this chat.',
    };
  }
  if (running) {
    return {
      visible: true,
      enabled: !input.dirty,
      label: 'Resume Switch Platform check',
      reason: input.dirty ? 'Save Carrier changes before you resume the check.' : null,
    };
  }
  const reason = input.dirty
    ? 'Save Carrier changes before you check the Switch Platform.'
    : !input.mapping.ready || input.mapping.drafts.some((draft) => draft.reviewStatus !== 'confirmed')
      ? 'Confirm every Carrier before you check the Switch Platform.'
      : input.folderPublication.disposition.orphanLinks > 0
        || input.folderPublication.disposition.linkedWithoutServiceType > 0
        ? 'Some Carrier links point to missing trunks or Service Types. Reload the Carrier setup.'
        : !input.folderPublication.foldersCurrent
          ? 'Publish the current Carrier folders before you check the Switch Platform.'
          : null;
  return {
    visible: true,
    enabled: reason === null,
    label: 'Check the Switch Platform for changes',
    reason,
  };
}

export const CARRIER_ORGANIZER_SETUP_REQUIRED_MESSAGE =
  'Carrier setup has not started. Run /organize-carriers to start Carrier discovery.';

export function carrierOrganizerSetupMessage(input: {
  mapping: CarrierInitializationMappingDrafts | null;
  error: string | null;
}): string | null {
  if (input.mapping || input.error) return null;
  return CARRIER_ORGANIZER_SETUP_REQUIRED_MESSAGE;
}

export function createDefaultVendorAssignments(input: Pick<
  CarrierInitializationMappingDrafts,
  'drafts' | 'groups' | 'unassignedVendors'
>): Record<number, string> {
  const targetsByCompanyId = new Map<number, Set<string>>();
  if (input.drafts.length > 0) {
    for (const draft of input.drafts) {
      const target = `carrier:${draft.id}`;
      for (const companyId of new Set(
        draft.assignedAccounts.map((account) => account.company.id)
      )) {
        const targets = targetsByCompanyId.get(companyId) ?? new Set<string>();
        targets.add(target);
        targetsByCompanyId.set(companyId, targets);
      }
    }
  } else {
    for (const group of input.groups) {
      targetsByCompanyId.set(
        group.providerCompanyId,
        new Set([`company:${group.providerCompanyId}`])
      );
    }
  }

  const assignments: Record<number, string> = {};
  for (const vendor of input.unassignedVendors) {
    if (vendor.suggestedProviderCompanyId === null) continue;
    const targets = targetsByCompanyId.get(vendor.suggestedProviderCompanyId);
    if (targets?.size === 1) {
      assignments[vendor.providerCarrierId] = [...targets][0]!;
    }
  }
  return assignments;
}

interface ProposalAccount {
  providerAccountId: number;
  company: { id: number };
  proposedService?: { serviceDefinitionId: string | null };
}

interface ProposalVendor {
  providerCarrierId: number;
  proposedProviderCompanyId?: number;
  proposedCarrierDraftId?: string;
  proposedNewCarrierName?: string;
  proposedService?: { serviceDefinitionId: string | null };
}

// Target prefix for a proposed Carrier that does not exist yet. It becomes a
// real Carrier only when an administrator accepts it.
export const PROPOSED_NEW_CARRIER_PREFIX = 'proposed-new:';

export function proposedNewCarrierTarget(name: string): string {
  return `${PROPOSED_NEW_CARRIER_PREFIX}${name.trim().toLocaleLowerCase('en-US')}`;
}

// Resolves each open vendor's proposal to a target: the one Carrier that holds
// the proposed SipNav company's Inbound trunks, an existing Carrier proposed by
// name, or a proposed new Carrier. A company split over several Carriers, or a
// Carrier that no longer exists, gives no target.
export function proposedVendorTargets(input: {
  accounts: ProposalAccount[];
  vendors: ProposalVendor[];
  accountAssignments: Record<number, string>;
  vendorAssignments: Record<number, string>;
  carrierTargets?: ReadonlySet<string>;
}): Record<number, string> {
  const targetsByCompany = new Map<number, Set<string>>();
  for (const account of input.accounts) {
    const target = input.accountAssignments[account.providerAccountId];
    if (!target) continue;
    const targets = targetsByCompany.get(account.company.id) ?? new Set<string>();
    targets.add(target);
    targetsByCompany.set(account.company.id, targets);
  }
  const result: Record<number, string> = {};
  for (const vendor of input.vendors) {
    if (input.vendorAssignments[vendor.providerCarrierId]) continue;
    if (vendor.proposedProviderCompanyId !== undefined) {
      const targets = targetsByCompany.get(vendor.proposedProviderCompanyId);
      if (targets?.size === 1) result[vendor.providerCarrierId] = [...targets][0]!;
    } else if (vendor.proposedCarrierDraftId !== undefined) {
      const target = `carrier:${vendor.proposedCarrierDraftId}`;
      if (input.carrierTargets?.has(target)) result[vendor.providerCarrierId] = target;
    } else if (vendor.proposedNewCarrierName) {
      result[vendor.providerCarrierId] = proposedNewCarrierTarget(vendor.proposedNewCarrierName);
    }
  }
  return result;
}

export interface CarrierProposalPlan {
  vendorIds: number[];
  accountServices: Record<number, string>;
  vendorServices: Record<number, string>;
  count: number;
}

// What "Accept proposals" changes for one Carrier: its proposed Outbound
// trunks, and a proposed Service Type for each of its trunks without one. A
// value that is already set is never replaced.
export function carrierProposalPlan(input: {
  target: string;
  accounts: ProposalAccount[];
  vendors: ProposalVendor[];
  accountAssignments: Record<number, string>;
  vendorAssignments: Record<number, string>;
  accountServiceDefinitionIds: Record<number, string>;
  vendorServiceDefinitionIds: Record<number, string>;
  vendorTargets: Record<number, string>;
}): CarrierProposalPlan {
  const vendorIds = input.vendors
    .filter((vendor) => input.vendorTargets[vendor.providerCarrierId] === input.target)
    .map((vendor) => vendor.providerCarrierId);
  const proposedVendorIds = new Set(vendorIds);
  const accountServices: Record<number, string> = {};
  for (const account of input.accounts) {
    const serviceDefinitionId = account.proposedService?.serviceDefinitionId;
    if (
      serviceDefinitionId
      && input.accountAssignments[account.providerAccountId] === input.target
      && !input.accountServiceDefinitionIds[account.providerAccountId]
    ) {
      accountServices[account.providerAccountId] = serviceDefinitionId;
    }
  }
  const vendorServices: Record<number, string> = {};
  for (const vendor of input.vendors) {
    const serviceDefinitionId = vendor.proposedService?.serviceDefinitionId;
    const inCarrier = input.vendorAssignments[vendor.providerCarrierId] === input.target
      || proposedVendorIds.has(vendor.providerCarrierId);
    if (
      serviceDefinitionId
      && inCarrier
      && !input.vendorServiceDefinitionIds[vendor.providerCarrierId]
    ) {
      vendorServices[vendor.providerCarrierId] = serviceDefinitionId;
    }
  }
  return {
    vendorIds,
    accountServices,
    vendorServices,
    count: vendorIds.length
      + Object.keys(accountServices).length
      + Object.keys(vendorServices).length,
  };
}

export type CarrierMappingTableRowStatus = 'proposed' | 'needs_decision' | 'done';

export interface CarrierMappingTableRow {
  key: string;
  direction: 'inbound' | 'outbound';
  providerRecordId: number;
  name: string;
  companyName: string | null;
  carrierTarget: string | null;
  carrierProposed: boolean;
  carrierReason: string | null;
  serviceDefinitionId: string | null;
  serviceProposed: boolean;
  serviceReason: string | null;
  status: CarrierMappingTableRowStatus;
}

interface TableAccount {
  providerAccountId: number;
  name: string;
  company: { id: number; name: string };
  proposedService?: { serviceDefinitionId: string | null; reason: string };
}

interface TableVendor {
  providerCarrierId: number;
  name: string;
  proposalReason?: string;
  proposedService?: { serviceDefinitionId: string | null; reason: string };
}

// One row per open trunk for the full-screen review table. A value the
// administrator set always wins; otherwise the proposal shows as a pre-selected
// value that is marked proposed and is not part of the edited mapping until it
// is accepted.
export function buildCarrierMappingTableRows(input: {
  accounts: TableAccount[];
  vendors: TableVendor[];
  accountAssignments: Record<number, string>;
  vendorAssignments: Record<number, string>;
  accountServiceDefinitionIds: Record<number, string>;
  vendorServiceDefinitionIds: Record<number, string>;
  vendorTargets: Record<number, string>;
}): CarrierMappingTableRow[] {
  const row = (
    base: Omit<CarrierMappingTableRow, 'status' | 'serviceDefinitionId' | 'serviceProposed' | 'serviceReason'>,
    savedServiceId: string | undefined,
    proposal: { serviceDefinitionId: string | null; reason: string } | undefined
  ): CarrierMappingTableRow => {
    const serviceProposed = !savedServiceId && Boolean(proposal?.serviceDefinitionId);
    const serviceDefinitionId = savedServiceId ?? proposal?.serviceDefinitionId ?? null;
    // A proposal is acceptable only when the trunk has a Carrier (saved or
    // proposed); a proposed Service Type alone needs a Carrier decision first.
    const status: CarrierMappingTableRowStatus = base.carrierTarget && (base.carrierProposed || serviceProposed)
      ? 'proposed'
      : base.carrierTarget && serviceDefinitionId
        ? 'done'
        : 'needs_decision';
    return {
      ...base,
      serviceDefinitionId,
      serviceProposed,
      serviceReason: savedServiceId ? null : proposal?.reason ?? null,
      status,
    };
  };
  const rows: CarrierMappingTableRow[] = [];
  for (const account of input.accounts) {
    rows.push(row({
      key: `inbound:${account.providerAccountId}`,
      direction: 'inbound',
      providerRecordId: account.providerAccountId,
      name: account.name,
      companyName: account.company.name,
      carrierTarget: input.accountAssignments[account.providerAccountId] ?? null,
      carrierProposed: false,
      carrierReason: null,
    }, input.accountServiceDefinitionIds[account.providerAccountId], account.proposedService));
  }
  for (const vendor of input.vendors) {
    const assigned = input.vendorAssignments[vendor.providerCarrierId];
    const proposed = assigned ? undefined : input.vendorTargets[vendor.providerCarrierId];
    rows.push(row({
      key: `outbound:${vendor.providerCarrierId}`,
      direction: 'outbound',
      providerRecordId: vendor.providerCarrierId,
      name: vendor.name,
      companyName: null,
      carrierTarget: assigned ?? proposed ?? null,
      carrierProposed: Boolean(proposed),
      carrierReason: proposed ? vendor.proposalReason ?? null : null,
    }, input.vendorServiceDefinitionIds[vendor.providerCarrierId], vendor.proposedService));
  }
  return rows.sort((left, right) => (
    left.name.localeCompare(right.name) || left.key.localeCompare(right.key)
  ));
}
